import React, { useEffect, useRef, useState } from "react";
import { Phone, AlertOctagon, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { getCurrentPosition, showLocalNotification, getNotificationPermission } from "../lib/native";

export const ACTIVE_SOS_KEY = "ts_active_sos";

// Máquina de estados explícita — "sent" solo se alcanza cuando el backend
// YA confirmó la alerta. Antes, el botón pasaba a "ENVIADO" apenas se
// cumplían los 3 segundos de mantener presionado, sin esperar la respuesta
// del POST /sos: si esa llamada fallaba (sin red, backend caído), la
// persona veía la confirmación visual pero la alerta nunca llegó a nadie.
const STATUS = { IDLE: "idle", PRESSING: "pressing", SENDING: "sending", SENT: "sent", ERROR: "error" };

export default function SOSButton({ groupId, bookingId, senderName, onAlert, initiallyActive = false }) {
  const [status, setStatus] = useState(initiallyActive ? STATUS.SENT : STATUS.IDLE);
  const [progress, setProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const timerRef = useRef(null);
  const startRef = useRef(0);

  const startPress = () => {
    if (status === STATUS.SENT || status === STATUS.SENDING || status === STATUS.PRESSING) return;
    setErrorMsg("");
    setStatus(STATUS.PRESSING);
    startRef.current = Date.now();
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startRef.current;
      const pct = Math.min(100, (elapsed / 3000) * 100);
      setProgress(pct);
      if (pct >= 100) {
        clearInterval(timerRef.current);
        fireSOS();
      }
    }, 50);
  };

  const cancelPress = () => {
    if (status !== STATUS.PRESSING) return;
    clearInterval(timerRef.current);
    setStatus(STATUS.IDLE);
    setProgress(0);
  };

  const fireSOS = async () => {
    setStatus(STATUS.SENDING);
    let location = null;
    try {
      location = await getCurrentPosition();
    } catch (e) {
      // No inventamos 0,0: es peor mandar a alguien al lugar equivocado que
      // no mandar ubicación. La alerta se manda igual, sin coordenadas.
      console.error("No se pudo obtener el GPS:", e);
    }
    try {
      const { data } = await api.post(
        "/sos",
        {
          group_id: groupId,
          booking_id: bookingId,
          sender_name: senderName || "Anónimo",
          latitude: location?.latitude ?? null,
          longitude: location?.longitude ?? null,
          accuracy: location?.accuracy ?? null,
          message: "SOS activado",
        },
        { timeout: 15000 } // sin esto, una red caída deja "ENVIANDO" pegado para siempre
      );
      setStatus(STATUS.SENT);
      onAlert && onAlert({ ...data, locationFailed: !location });
      // Guarda la alerta activa para que sobreviva a cerrar/reabrir la app
      // (antes se perdía por completo — solo vivía en el estado de React).
      localStorage.setItem(ACTIVE_SOS_KEY, JSON.stringify({
        id: data.id, group_id: groupId || null, booking_id: bookingId || null,
        sender_name: senderName || "Anónimo",
      }));
      // Notificación local (funciona igual en web y en la app nativa) — con
      // un botón real para volver directo al estado de la alerta, no solo
      // al tocar el cuerpo de la notificación.
      const perm = await getNotificationPermission();
      if (perm === "granted") {
        showLocalNotification(
          "SOS enviado",
          location
            ? `Alerta enviada a tu grupo. Ver ubicación: ${data.maps_url}`
            : "Alerta enviada a tu grupo. No se pudo capturar tu ubicación GPS — descríbela por teléfono.",
          { tag: "cherry-sos-active", url: "/sos", actions: [{ action: "view-sos", title: "Ver estado" }] }
        );
      }
    } catch (e) {
      console.error(e);
      setStatus(STATUS.ERROR);
      setErrorMsg(
        e?.response?.data?.detail || "No se pudo enviar la alerta. Revisa tu conexión y mantén presionado de nuevo."
      );
    }
  };

  const triggered = status === STATUS.SENT;
  const pressing = status === STATUS.PRESSING;
  const sending = status === STATUS.SENDING;
  const hasError = status === STATUS.ERROR;

  useEffect(() => () => clearInterval(timerRef.current), []);
  // initiallyActive llega después del primer render (SOSPage confirma con
  // el backend que la alerta sigue activa antes de avisarnos) — hay que
  // sincronizarlo cuando cambie, no solo usarlo como valor inicial. Antes
  // solo reaccionaba cuando se volvía true: al resolver la alerta (o cuando
  // la alerta activa pasaba a ser de OTRO miembro), el botón se quedaba
  // pegado en "ENVIADO" hasta recargar la página.
  useEffect(() => {
    if (initiallyActive) setStatus(STATUS.SENT);
    else setStatus((s) => (s === STATUS.SENT ? STATUS.IDLE : s));
  }, [initiallyActive]);

  return (
    <div className="flex flex-col items-center gap-6" data-testid="sos-container">
      <button
        onMouseDown={startPress}
        onMouseUp={cancelPress}
        onMouseLeave={cancelPress}
        onTouchStart={startPress}
        onTouchEnd={cancelPress}
        data-testid="sos-button"
        aria-busy={sending}
        className={`relative w-56 h-56 rounded-full flex items-center justify-center transition-all duration-300 select-none ${
          triggered
            ? "bg-[#FF3B30] sos-active"
            : sending
            ? "bg-[#FF3B30]/50 border-2 border-[#FF3B30] animate-pulse"
            : hasError
            ? "bg-black border-2 border-[#FFB020] sos-idle"
            : pressing
            ? "bg-[#FF3B30]/30 border-2 border-[#FF3B30]"
            : "bg-black border border-[var(--brand-cyan,#FF85AC)]/60 sos-idle hover:scale-105"
        }`}
        style={{ touchAction: "none" }}
      >
        {/* progress ring */}
        {pressing && (
          <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="47" fill="none" stroke="rgba(255,59,48,0.15)" strokeWidth="2" />
            <circle
              cx="50" cy="50" r="47" fill="none"
              stroke="#FF3B30" strokeWidth="2.5" strokeLinecap="round"
              strokeDasharray={`${(progress * 295.3) / 100} 295.3`}
            />
          </svg>
        )}
        <div className="text-center pointer-events-none">
          {sending ? (
            <Loader2 className="mx-auto mb-2 text-white animate-spin" strokeWidth={1.4} size={42} />
          ) : (
            <AlertOctagon
              className={`mx-auto mb-2 ${triggered ? "text-white" : hasError ? "text-[#FFB020]" : "text-[var(--brand-cyan,#FF85AC)]"}`}
              strokeWidth={1.4}
              size={42}
            />
          )}
          <div className={`font-display tracking-[0.25em] ${triggered || sending ? "text-white text-2xl" : hasError ? "text-[#FFB020] text-xl" : "text-[#F5F5F5] text-xl"}`}>
            {triggered ? "ENVIADO" : sending ? "ENVIANDO…" : hasError ? "NO SE ENVIÓ" : pressing ? "MANTÉN" : "SOS"}
          </div>
          <div className={`text-[10px] uppercase tracking-[0.3em] mt-1 ${triggered || sending ? "text-white/80" : hasError ? "text-[#FFB020]/80" : "text-neutral-500"}`}>
            {triggered
              ? "Tu grupo fue notificado"
              : sending
              ? "Un momento…"
              : hasError
              ? "Mantén de nuevo para reintentar"
              : "Mantén 3 segundos"}
          </div>
        </div>
      </button>

      {hasError && (
        <p className="text-sm text-[#FFB020] text-center max-w-xs -mt-2" data-testid="sos-error">
          {errorMsg}
        </p>
      )}

      <a
        href="tel:911"
        data-testid="call-911-btn"
        className="flex items-center gap-2.5 px-6 py-3 rounded-full border border-[#FF3B30]/50 hover:bg-[#FF3B30]/10 text-[#FF3B30] transition-all"
      >
        <Phone className="w-4 h-4" strokeWidth={1.8} />
        <span className="font-display text-sm tracking-[0.3em] uppercase">Llamar al 911</span>
      </a>
    </div>
  );
}
